import { useTranslation } from 'react-i18next';
import { useLocation, useParams } from 'react-router-dom';
import { SxProps } from '@mui/material';
import { useMemo, useState } from 'react';
import { mdiLightbulbOutline } from '@mdi/js';

import { PageTitle } from 'common/components/PageTitle';
import {
    IPortfolioItemMatrixListItemFragment,
    useCurrentUserQuery,
} from 'graphql/types';
import { IUserProfileSectionProps } from 'user/types';
import { List } from 'common/components/List';
import { Accordion, AccordionSummary } from 'common/components/Accordion';
import { ExtraCategoryLabels } from 'organisation/components/ExtraCategoryLabels';
import { ChipGroup } from 'common/components/Chip';
import { PortfolioListItem } from 'user/components/PortfolioListItem';
import { useGlobalDrawer } from 'common/hooks/useGlobalDrawer';
import { IGlobalDrawerType } from 'common/types';
import { PortfolioItemProgressScoreChip } from 'user/components/PortfolioItemProgressScoreChip';
import { getGroupsState, TGroupsState } from 'user/utils/getGroupsState';
import { Pagination } from 'common/components/Pagination';
import { useSkillGroupsList } from 'skill/hooks/useSkillGroupsList';
import { BoxLoader } from 'common/components/Loader';
import { NoResults } from 'common/components/NoResults';
import { DEFAULT_PAGINATION_SETTINGS } from 'common/hooks/usePagination';
import { useSkillReportList } from 'skill/hooks/useSkillReportList';

const listStyle: SxProps = {
    'div:first-of-type .MuiListItem-root': {
        pt: 0,
    },
};

const GROUP_TYPE = 'portfolioSkills';

export const PortfolioSkills = ({ name }: IUserProfileSectionProps) => {
    const { data } = useCurrentUserQuery();
    const [translate] = useTranslation();
    const { id: userIdParam } = useParams();
    const { openGlobalDrawer } = useGlobalDrawer();
    const location = useLocation();
    const [expanded, setExpanded] = useState<TGroupsState>(
        getGroupsState(GROUP_TYPE)
    );

    const userId = userIdParam || data?.currentUser?.id;

    const {
        skillGroups,
        skillGroupsCount,
        loading: skillGroupsLoading,
        paginationSettings,
        setPage,
    } = useSkillGroupsList({ variables: { userIds: [userId] }, skip: !userId });

    const skillGroupIds = skillGroups.map((group) => group.id);
    const count = skillGroupsCount || 0;

    const { items: skillPortfolioItems, loading: skillsLoading } =
        useSkillReportList({
            queryVariables: {
                userIds: userId ? [userId] : [],
                skillIds: skillGroups
                    .map((group) => group.skills?.map((skill) => skill.id))
                    .filter(Boolean)
                    .flat(),
                withTitle: true,
            },
            skip: !userId || !skillGroupIds.length,
        });

    const handleExpandGroup = (groupId: string) => {
        const isExpanded = !(groupId in expanded) || expanded[groupId];

        const newPortfolioItemGroupsState = {
            ...expanded,
            [groupId]: !isExpanded,
        };

        setExpanded(newPortfolioItemGroupsState);

        localStorage.setItem(
            `${GROUP_TYPE}ItemGroupsState`,
            JSON.stringify(newPortfolioItemGroupsState)
        );
    };

    const loading = skillGroupsLoading || skillsLoading;

    const groups = useMemo(
        () =>
            skillGroups.map((group) => {
                const { skills } = group;

                const skillItems = skills
                    ?.map((skill) => {
                        const portfolioItem = skillPortfolioItems.find(
                            (item) => item.relatedId === skill.id
                        ) as IPortfolioItemMatrixListItemFragment & {
                            title: string;
                        };

                        return {
                            portfolioItem,
                            thumbnail: skill.thumbnail,
                        };
                    })
                    .filter(Boolean);

                return {
                    ...group,
                    skills: skillItems,
                };
            }),
        [skillGroups, skillPortfolioItems]
    );

    return (
        <>
            <PageTitle>{`${translate('portfolioSkillsPage.pageTitle')} - ${name}`}</PageTitle>

            {!loading && (
                <>
                    {groups.map(({ id, name, extraCategoryValues, skills }) => {
                        if (!skills?.length) return null;

                        const isExpanded = !(id in expanded) || expanded[id];

                        return (
                            <Accordion
                                expanded={isExpanded}
                                key={`group-${id}`}
                                summary={
                                    <AccordionSummary
                                        subtitle={
                                            extraCategoryValues ? (
                                                <ChipGroup sx={{ mt: 1 }}>
                                                    <ExtraCategoryLabels
                                                        extraCategoryValues={
                                                            extraCategoryValues
                                                        }
                                                    />
                                                </ChipGroup>
                                            ) : undefined
                                        }
                                        sx={{ mb: 0 }}
                                        title={name}
                                    />
                                }
                                onChange={() => handleExpandGroup(id)}
                            >
                                <List noBorder sx={listStyle}>
                                    {skills.map((skill) => {
                                        const { portfolioItem, thumbnail } =
                                            skill;
                                        const { id, title } =
                                            portfolioItem || {};

                                        return (
                                            <PortfolioListItem
                                                icon={mdiLightbulbOutline}
                                                image={thumbnail?.url || ''}
                                                key={`portfolioItem-${id}`}
                                                secondaryAction={
                                                    <PortfolioItemProgressScoreChip
                                                        portfolioItem={
                                                            portfolioItem
                                                        }
                                                    />
                                                }
                                                title={title || ''}
                                                onClick={() => {
                                                    openGlobalDrawer(
                                                        {
                                                            type: IGlobalDrawerType.PortfolioItem,
                                                            itemId: id,
                                                        },
                                                        location
                                                    );
                                                }}
                                            />
                                        );
                                    })}
                                </List>
                            </Accordion>
                        );
                    })}
                </>
            )}

            {loading && <BoxLoader />}

            {!loading && count === 0 && <NoResults variant="SKILLS" />}

            {!loading && count > DEFAULT_PAGINATION_SETTINGS.first && (
                <Pagination
                    page={paginationSettings.page}
                    pageAmount={paginationSettings.pageAmount}
                    totalsAmount={count}
                    totalsText={translate('result', {
                        count: count || 0,
                    })}
                    onChange={(page: number) => {
                        setPage(page);
                    }}
                />
            )}
        </>
    );
};
